import { useAnimatedOpacityOnly } from '@ui/animated/useAnimatedOpacity'
import useAnimatedPress from '@ui/animated/useAnimatedPress'
import Gradient from '@ui/effects/Gradient'
import useRWD from '@ui/theme/useRWD'
import Button from '@ui/buttons/Button'
import SafePressable from '@ui/buttons/SafePressable'
import { Customer } from '@ca/common/types'
import { colors as c } from '@ui/colors'
import Dropdown from '@ui/elements/Dropdown'
import TileGlass from '@ui/tiles/TileGlass'
import Typography from '@ui/typography'
import { useStaffLoginCustomer } from 'hooks/useStaffLoginCustomer'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import UserContactState from '@ui/tiles/TileUser/UserContactState'

interface Props {
  data: Customer
  verify: ReturnType<typeof useStaffLoginCustomer>
  onPress?: () => void
}

const borderRadius = 16

export default function TileUser({ data, verify, onPress }: Props) {
  const { t } = useTranslation(['auth'])
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const { animation, onPressIn, onPressOut } = useAnimatedPress()
  const { tiles } = useRWD()
  const width = tiles.user.width
  const height = tiles.user.height
  const widthDropdown = width - 12

  const styleActive = useAnimatedOpacityOnly(dropdownOpen)

  const {
    first_name,
    last_name,
    email,
    mobile,
    ref,
    address,
    phone_state,
    email_state,
    phone,
  } = data
  return (
    <SafePressable
      onPress={onPress ? onPress : () => setDropdownOpen((prev) => !prev)}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      className="relative inset-0 flex-1"
    >
      <View className="absolute left-0 top-0">
        <Dropdown
          open={dropdownOpen}
          setOpen={setDropdownOpen}
          popoverProps={{
            position: ['bottom', 'right'],
            offset: [0, 5],
          }}
          width={widthDropdown}
          offsetX={widthDropdown + 6}
          items={[
            {
              icon: 'mail',
              label: t('auth:auth.email'),
              disabled: !email,
              onPress: () => verify.request(data, 'email'),
            },
            {
              icon: 'phone',
              label: t('auth:auth.phone'),
              disabled: !mobile,
              onPress: () => verify.request(data, 'phone'),
            },
            {
              icon: 'id',
              label: t('auth:auth.staff'),
              disabled: false, // THIS SHOULD BE: isDev ? false : !!(email || mobile),
              onPress: () => verify.request(data, 'staff'),
            },
          ]}
        />
      </View>
      <TileGlass
        animation={animation}
        style={[{ width, height, borderRadius }]}
        gradient={
          <>
            <Gradient
              colors={[c.white, c.white25]}
              start={[0, 0]}
              end={[1, 1]}
              style={{ borderRadius }}
            />
            <Gradient
              colors={[c.gray[75], c.gray[100]]}
              start={[0, 0]}
              end={[1, 1]}
              style={[{ borderRadius }, styleActive]}
            />
          </>
        }
      >
        <View className="relative h-full w-full justify-between gap-2 px-8 py-6">
          <View className="gap-1">
            <View className="flex flex-row items-start justify-between">
              <Typography variant={'MDB'}>
                {first_name} {last_name}
              </Typography>
            </View>
            <Typography variant={'SM'} className="color-desc">
              {ref} {address ? `• ${address.city} • ${address.zip}` : ''}
            </Typography>
          </View>
          <View className="flex-1 justify-end gap-0">
            <UserContactState type="email" data={email} state={email_state} />
            <UserContactState type="phone" data={mobile} state={phone_state} />
            <UserContactState type="phone" data={phone} />
          </View>
          <View
            className="absolute"
            style={{ right: 32, width: 40, top: (height - 32) / 2 }}
          >
            {!!onPress && (
              <Button
                onPress={onPress}
                iconStart="arrow-narrow-right"
                size="sm"
                variant="outlined"
              />
            )}
            {!onPress && (
              <Button
                noninteractive
                animation={animation}
                size="xs"
                variant="outlined"
                iconStart="arrow-narrow-right"
              />
            )}
          </View>
        </View>
      </TileGlass>
    </SafePressable>
  )
}
